import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import './AppChip.css'

export const APP_CHIP_BASE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 72,
  height: 22,
  padding: '0 8px',
  borderRadius: '999px',
  boxSizing: 'border-box',
  fontFamily: "var(--app-chip-font-family, 'Geist', 'Inter', 'Segoe UI', sans-serif)",
  fontSize: 'var(--app-chip-status-font-size, 10px)',
  fontWeight: 'var(--app-chip-status-font-weight, 700)',
  lineHeight: 1,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}

export function buildAppChipSx({
  backgroundColor,
  color,
  minWidth,
  height,
  padding,
  borderRadius,
  fontSize,
  fontWeight,
  lineHeight,
  textTransform,
  whiteSpace,
  sx = {}
} = {}) {
  return {
    ...APP_CHIP_BASE_SX,
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(color ? { color } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(padding !== undefined ? { padding } : {}),
    ...(borderRadius !== undefined ? { borderRadius } : {}),
    ...(fontSize !== undefined ? { fontSize } : {}),
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(lineHeight !== undefined ? { lineHeight } : {}),
    ...(textTransform !== undefined ? { textTransform } : {}),
    ...(whiteSpace !== undefined ? { whiteSpace } : {}),
    ...sx
  }
}

/**
 * Chip base reutilizable.
 *
 * Usa `renderAs="chip"` cuando quieres el comportamiento de MUI `Chip`.
 * Usa `renderAs="span"` cuando solo necesitas una pastilla visual dentro del texto o layout.
 */
export default function AppChip({
  label,
  renderAs = 'chip',
  size = 'small',
  sx = {},
  chipLabelSx = {},
  component = 'span',
  className,
  variant,
  onDelete,
  deleteIcon,
  ariaLabel,
  ...chipProps
}) {
  // Las variantes usan CSS; `sx` queda disponible únicamente para excepciones puntuales.
  const resolvedSx = variant ? sx : buildAppChipSx({ sx })
  const resolvedClassName = ['app-chip', variant && `app-chip--${variant}`, className]
    .filter(Boolean)
    .join(' ')

  if (renderAs === 'span') {
    return (
      <Box component={component} className={resolvedClassName} sx={resolvedSx}>
        {label}
      </Box>
    )
  }

  return (
    <Chip
      {...chipProps}
      label={label}
      size={size}
      className={resolvedClassName}
      onDelete={onDelete}
      deleteIcon={deleteIcon}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      sx={{
        ...resolvedSx,
        ...(variant
          ? { '& .MuiChip-label': { px: 0, py: 0, ...chipLabelSx } }
          : {
              '&.MuiChip-sizeSmall': { height: resolvedSx.height },
              '& .MuiChip-label': {
                px: 0,
                py: 0,
                fontSize: resolvedSx.fontSize,
                lineHeight: `${resolvedSx.height}px`,
                ...chipLabelSx
              }
            })
      }}
    />
  )
}
